import { createContext, useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { User, LoginResult } from '../api/auth';
import { getMe, login as apiLogin, logout as apiLogout, isTwoFactorChallenge, getCsrfToken } from '../api/auth';
import { apiClient } from '../api/client';
import { BOOTSTRAP_TIMEOUT_MS, classifyBootstrapError } from './AuthBootstrap';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  /** Set when the session couldn't be checked because the API was unavailable (not signed out). */
  bootError: string | null;
  /** Re-run the session check after a `bootError`. */
  retryBootstrap: () => Promise<void>;
  login: (email: string, password: string) => Promise<LoginResult>;
  loginWithUser: (user: User, csrfToken: string) => void;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [bootError, setBootError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  /**
   * Bootstrap the session. Both calls run in parallel under a short timeout
   * (see AuthBootstrap.ts): a stalled API used to leave a full-page spinner up
   * for minutes. Only a 401 means signed out; anything else is an outage and
   * surfaces as `bootError`, which the shell shows with a Retry button.
   */
  const bootstrap = useCallback(async () => {
    setIsLoading(true);
    setBootError(null);
    const [csrf, me] = await Promise.allSettled([
      getCsrfToken({ signal: AbortSignal.timeout(BOOTSTRAP_TIMEOUT_MS) }),
      getMe({ signal: AbortSignal.timeout(BOOTSTRAP_TIMEOUT_MS) }),
    ]);
    if (me.status === 'fulfilled') {
      setUser(me.value);
      // The api client re-fetches a missing CSRF token on the first 403, so a
      // failed token fetch alone doesn't block a signed-in user.
      if (csrf.status === 'rejected') console.warn('CSRF token fetch failed during bootstrap', csrf.reason);
    } else {
      const failure = classifyBootstrapError(me.reason);
      setUser(null);
      if (failure.kind === 'unavailable') setBootError(failure.message);
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  const login = useCallback(async (email: string, password: string): Promise<LoginResult> => {
    const response = await apiLogin(email, password);
    if (!isTwoFactorChallenge(response)) {
      // Clear any stale queries from before login
      queryClient.clear();
      setUser(response.user);
    }
    return response;
  }, [queryClient]);

  const loginWithUser = useCallback((user: User, csrfToken: string) => {
    apiClient.setCsrfToken(csrfToken);
    queryClient.clear();
    setUser(user);
  }, [queryClient]);

  const logout = useCallback(async () => {
    await apiLogout();
    queryClient.clear();
    setUser(null);
  }, [queryClient]);

  /** Re-fetch the current user (e.g. after toggling optional sections in Settings). */
  const refreshUser = useCallback(async () => {
    const currentUser = await getMe();
    setUser(currentUser);
  }, []);

  return (
    <AuthContext.Provider value={{ user, isLoading, bootError, retryBootstrap: bootstrap, login, loginWithUser, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}
